import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { brandingApi } from '../../lib/api';
import { APP_NAME, APP_VERSION, BRAND_BACKDROP, BRAND_LOGO, BRAND_NAME } from '../../lib/brand';
import type { Branding } from '../../lib/types';

/**
 * What the portal calls itself and the pictures it shows. The superadmin sets
 * them in the user menu → Customization; a field left empty there keeps the value
 * the client was built with (lib/brand.ts, from .env).
 *
 * The last answer is kept in this browser, so a reload does not flash the
 * built-in name before the API has answered.
 */
interface BrandingValue {
  brandName: string;
  appName: string;
  logo: string;
  backdrop: string;
  /** The product name beside the logo in the navbar; always shown when there is no logo. */
  showName: boolean;
  appVersion: string;
  /** What the superadmin saved, or null when nothing has been. */
  saved: Branding | null;
  /** Put a just-saved branding on screen without asking the API again. */
  apply: (next: Branding) => void;
}

const CACHE_KEY = 'wxp.branding';

function readCache(): Branding | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as Branding) : null;
  } catch {
    return null;
  }
}

function writeCache(b: Branding) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(b));
  } catch {
    /* storage unavailable — the next load asks the API again */
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export const BrandingContext = createContext<BrandingValue | null>(null);

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [saved, setSaved] = useState<Branding | null>(readCache);

  const apply = useCallback((next: Branding) => {
    setSaved(next);
    writeCache(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    brandingApi
      .get()
      .then((b) => {
        if (!cancelled) apply(b);
      })
      .catch(() => {
        // An API without branding, or not reachable: the built-in values stay.
      });
    return () => {
      cancelled = true;
    };
  }, [apply]);

  const value = useMemo<BrandingValue>(
    () => ({
      brandName: saved?.brandName || BRAND_NAME,
      appName: saved?.appName || APP_NAME,
      logo: saved?.logo || BRAND_LOGO,
      backdrop: saved?.backdrop || BRAND_BACKDROP,
      showName: saved?.showName ?? false,
      appVersion: APP_VERSION,
      saved,
      apply,
    }),
    [saved, apply],
  );

  useEffect(() => {
    document.title = value.appName;
  }, [value.appName]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}
